import { useCallback, useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { NavBar } from './NavBar';
import { FolderTree } from './FolderTree';
import { api } from '../api/client';
import { FolderTreeNode } from '../api/types';
import { clamp, startHorizontalResize } from '../utils/resize';
import { hasPendingSaves } from '../utils/pendingSave';
import { DIAGRAM_MOVED_EVENT } from '../utils/dnd';

interface LayoutContext {
  tree: FolderTreeNode[];
  refreshTree: () => void;
}

export function useLayoutContext() {
  return useOutletContext<LayoutContext>();
}

const SIDEBAR_WIDTH_KEY = 'gantt:sidebarWidth';
const DEFAULT_SIDEBAR_WIDTH = 280;
const MIN_SIDEBAR_WIDTH = 180;
const MAX_SIDEBAR_WIDTH = 560;

function loadSidebarWidth(): number {
  try {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? clamp(parseInt(saved, 10), MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH) : DEFAULT_SIDEBAR_WIDTH;
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

export function Layout() {
  const [tree, setTree] = useState<FolderTreeNode[]>([]);
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);

  const refreshTree = useCallback(() => {
    api.get<FolderTreeNode[]>('/folders/tree').then(setTree);
  }, []);

  useEffect(() => {
    refreshTree();
  }, [refreshTree]);

  // Un déplacement ou un renommage de diagramme peut être lancé depuis
  // l'arborescence elle-même (glisser-déposer entre dossiers, renommage en
  // ligne) ou depuis la grille d'un dossier — dans tous les cas l'arbre doit
  // refléter le changement sans que l'utilisateur ait à changer de page.
  useEffect(() => {
    window.addEventListener(DIAGRAM_MOVED_EVENT, refreshTree);
    return () => window.removeEventListener(DIAGRAM_MOVED_EVENT, refreshTree);
  }, [refreshTree]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasPendingSaves()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    } catch {
      // stockage indisponible (navigation privée...) : la largeur reste simplement non persistée.
    }
  }, [sidebarWidth]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    startHorizontalResize(e.clientX, (delta) => {
      setSidebarWidth((w) => clamp(w + delta, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH));
    });
  };

  return (
    <div className="app-shell">
      <NavBar />
      <div className="app-body">
        <aside className="sidebar" style={{ width: sidebarWidth }}>
          <FolderTree nodes={tree} />
        </aside>
        <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} />
        <main className="main-content">
          <Outlet context={{ tree, refreshTree } satisfies LayoutContext} />
        </main>
      </div>
    </div>
  );
}
